// src/constants/learningAreas.ts
//
// Single source of truth for "Learning Areas" (formerly called, interchangeably,
// "Departments" and "Curriculum Areas" across the app).
//
// The authoritative, editable list lives in the `curriculum_areas` table and is
// exposed at runtime via SchoolConfigContext (`useSchoolConfig().curriculumAreas`).
// The constants below are:
//   1. a fallback list (display order + names) for when config has not loaded, and
//   2. the target vocabulary for `normalizeLearningArea`, which maps the messy,
//      free-text values stored on grid classes and staff records (mixed casing,
//      slash-joined combinations like "Science / STEAM", and school-specific
//      abbreviations like "HPE") onto ONE canonical learning-area name.
//
// Nothing here mutates data. Consumers use these helpers to unify the LISTS they
// show (dropdowns, filters, report groupings) so every surface presents the same
// slash-free set of learning areas, regardless of how the underlying value was typed.

export interface CanonicalLearningArea {
  /** Short code, matches curriculum_areas.code */
  code: string;
  /** Canonical display name, matches curriculum_areas.name */
  name: string;
}

/**
 * Fallback canonical list, mirroring the `curriculum_areas` table
 * (Salesian College Sunbury) in display order. The live list from
 * SchoolConfigContext takes precedence at runtime — see `useLearningAreas`.
 */
export const CANONICAL_LEARNING_AREAS: CanonicalLearningArea[] = [
  { code: 'ART', name: 'Arts' },
  { code: 'AGR', name: 'Agriculture' },
  { code: 'ENG', name: 'English' },
  { code: 'HUM', name: 'Humanities' },
  { code: 'LAN', name: 'Languages' },
  { code: 'MAT', name: 'Mathematics' },
  { code: 'SCI', name: 'Science' },
  // "HPE" is this school's canonical short form; a DB trigger
  // (convert_pe_to_hpe) enforces it on grid class data.
  { code: 'HPE', name: 'HPE' },
  { code: 'REL', name: 'Religion' },
  { code: 'STE', name: 'STEAM' },
  { code: 'VMA', name: 'Vocational Major' },
  { code: 'LEN', name: 'Learning Diversity' },
  { code: 'ORA', name: 'Oratory' },
  { code: 'RUA', name: 'RUAH' },
  { code: 'RET', name: 'Retreat' },
];

export const CANONICAL_LEARNING_AREA_NAMES: string[] =
  CANONICAL_LEARNING_AREAS.map((a) => a.name);

/**
 * Maps an UPPER-CASED, whitespace-collapsed raw value onto a canonical name.
 * Covers every distinct variant observed in production plus common aliases.
 * Keys are compared case-insensitively; values MUST match a canonical name above.
 */
const SYNONYM_MAP: Record<string, string> = {
  // Arts
  ART: 'Arts',
  ARTS: 'Arts',
  'CREATIVE ARTS': 'Arts',
  'VISUAL ARTS': 'Arts',
  MUSIC: 'Arts',
  DRAMA: 'Arts',
  // Agriculture
  AGR: 'Agriculture',
  AG: 'Agriculture',
  AGRICULTURE: 'Agriculture',
  // English
  ENG: 'English',
  ENGLISH: 'English',
  // Humanities (History / Geography roll up to Humanities at this school)
  HUM: 'Humanities',
  HUMANITIES: 'Humanities',
  HISTORY: 'Humanities',
  GEOGRAPHY: 'Humanities',
  HASS: 'Humanities',
  // Languages
  LAN: 'Languages',
  LANGUAGE: 'Languages',
  LANGUAGES: 'Languages',
  LOTE: 'Languages',
  // Mathematics
  MAT: 'Mathematics',
  MATH: 'Mathematics',
  MATHS: 'Mathematics',
  MATHEMATICS: 'Mathematics',
  // Science
  SCI: 'Science',
  SCIENCE: 'Science',
  // Health & PE
  HPE: 'HPE',
  PE: 'HPE',
  PDHPE: 'HPE',
  HEALTH: 'HPE',
  'HEALTH & PE': 'HPE',
  'HEALTH AND PE': 'HPE',
  'PHYSICAL EDUCATION': 'HPE',
  'HEALTH & PHYSICAL EDUCATION': 'HPE',
  'HEALTH AND PHYSICAL EDUCATION': 'HPE',
  'HEALTH AND PERSONAL DEVELOPMENT': 'HPE',
  // Religion
  REL: 'Religion',
  RE: 'Religion',
  RELIGION: 'Religion',
  'RELIGIOUS EDUCATION': 'Religion',
  // STEAM (technology / design roll up to STEAM at this school)
  STE: 'STEAM',
  STEAM: 'STEAM',
  TECH: 'STEAM',
  TECHNOLOGY: 'STEAM',
  TECHNOLOGIES: 'STEAM',
  'DESIGN & TECHNOLOGY': 'STEAM',
  ICT: 'STEAM',
  // Vocational Major / VET
  VM: 'Vocational Major',
  VMA: 'Vocational Major',
  VET: 'Vocational Major',
  VCAL: 'Vocational Major',
  VCE: 'Vocational Major',
  VOCATIONAL: 'Vocational Major',
  'VOCATIONAL MAJOR': 'Vocational Major',
  // Learning Diversity
  LEN: 'Learning Diversity',
  'LEARNING SUPPORT': 'Learning Diversity',
  'LEARNING DIVERSITY': 'Learning Diversity',
  'LEARNING DIVERSITY & SUPPORT': 'Learning Diversity',
  // Oratory
  ORA: 'Oratory',
  ORATORY: 'Oratory',
};

/** Set of canonical names (upper-cased) for fast exact-match checks. */
const CANONICAL_UPPER = new Set(
  CANONICAL_LEARNING_AREA_NAMES.map((n) => n.toUpperCase())
);

function cleanToken(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * Resolve a single (already slash-split) token to a canonical learning-area name.
 * Returns null when the token is not a recognised learning area (e.g. "Admin",
 * "General", a stray day-of-week, an ID number, or a grid/program name like RUAH).
 */
function resolveToken(token: string): string | null {
  const cleaned = cleanToken(token);
  if (!cleaned) return null;
  const upper = cleaned.toUpperCase();

  // Already canonical?
  if (CANONICAL_UPPER.has(upper)) {
    return CANONICAL_LEARNING_AREA_NAMES.find((n) => n.toUpperCase() === upper) ?? null;
  }
  // Known synonym / abbreviation?
  if (SYNONYM_MAP[upper]) return SYNONYM_MAP[upper];

  return null;
}

/**
 * Normalise a raw, free-text learning-area value onto ONE canonical name.
 *
 * - Trims and collapses whitespace, case-insensitive.
 * - For slash-joined values ("Science / STEAM", "VM/VET") the FIRST recognised
 *   segment is treated as the primary learning area.
 * - Returns null when nothing in the value maps to a canonical learning area,
 *   so callers can bucket it explicitly (e.g. as "Other") rather than inventing
 *   a spurious area.
 *
 * Never mutates or persists anything — pure function.
 */
export function normalizeLearningArea(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const segments = String(raw).split(/[/,;]|(?:\s+&\s+)/);
  for (const segment of segments) {
    const resolved = resolveToken(segment);
    if (resolved) return resolved;
  }
  return null;
}

/**
 * Expand a raw value into ALL canonical learning areas it references, in order,
 * de-duplicated. "ENGLISH / HUMANITIES" -> ["English", "Humanities"].
 * Empty array when none are recognised.
 */
export function splitLearningAreas(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const out: string[] = [];
  for (const segment of String(raw).split(/[/,;]|(?:\s+&\s+)/)) {
    const resolved = resolveToken(segment);
    if (resolved && !out.includes(resolved)) out.push(resolved);
  }
  return out;
}

/** True when `raw` resolves to a recognised canonical learning area. */
export function isLearningArea(raw: string | null | undefined): boolean {
  return normalizeLearningArea(raw) !== null;
}

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * A clean, display-ready learning-area label for grouping/report axes.
 *
 * - Canonical learning areas map to their canonical name (so "MATHEMATICS",
 *   "Mathematics" and "maths" all collapse to one bucket).
 * - Values that are not canonical learning areas but are still meaningful
 *   (e.g. grid/program scopes like "RUAH" or "RETREAT") are preserved as a
 *   tidied, title-cased label of their primary segment — never slash-joined.
 * - Empty/blank falls back to `fallback` ("Unassigned" by default).
 */
export function displayLearningArea(
  raw: string | null | undefined,
  fallback = 'Unassigned'
): string {
  const canonical = normalizeLearningArea(raw);
  if (canonical) return canonical;
  if (!raw) return fallback;
  const primary = cleanToken(String(raw).split(/[/,;]/)[0] || '');
  return primary ? titleCase(primary) : fallback;
}

/**
 * All canonical learning areas referenced by a staff record's free-text
 * department / curriculum-area fields, de-duplicated. Used to bucket staff into
 * learning areas for reporting without losing multi-area membership.
 */
export function learningAreasFromStaffFields(
  ...rawFields: Array<string | null | undefined>
): string[] {
  const out: string[] = [];
  for (const field of rawFields) {
    for (const area of splitLearningAreas(field)) {
      if (!out.includes(area)) out.push(area);
    }
  }
  return out;
}
